import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { MenuConfigService } from 'src/app/services/menu-config.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';

interface SectionSummary {
  title: string;
  description: string;
  route: string;
  icon: string;
}

interface HeroDot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('heroCanvas', { static: false }) heroCanvas?: ElementRef<HTMLCanvasElement>;

  private destroy$ = new Subject<void>();
  private heroCtx: CanvasRenderingContext2D | null = null;
  private heroDots: HeroDot[] = [];
  private animationFrameId: number | null = null;
  private readonly heroDotsCount = 55;

  sections: SectionSummary[] = [];

  // Mapeo de iconos para las secciones
  private iconMapping: { [key: string]: string } = {
    'abautMe': 'iconsminds-administrator',
    'skills': 'iconsminds-gear',
    'experience': 'iconsminds-file-clipboard-file---text',
    'projects': 'iconsminds-rocket',
    'gallery': 'iconsminds-trophy',
    'clients': 'iconsminds-conference'
  };

  // Descripciones personalizadas para cada sección
  private descriptionMapping: { [key: string]: string } = {
    'abautMe': 'Conoce nuestra misión, visión y valores. Somos un equipo comprometido con la innovación y la excelencia en soluciones tecnológicas.',
    'skills': 'Ofrecemos automatización industrial, domótica, desarrollo de software, integración de sistemas y consultoría tecnológica personalizada.',
    'experience': 'Años de trayectoria implementando soluciones tecnológicas exitosas en diversos sectores industriales y comerciales.',
    'projects': 'Descubre nuestro portafolio de proyectos de automatización, integración tecnológica y desarrollo de soluciones digitales.',
    'gallery': 'Avalados por certificaciones internacionales que garantizan la calidad y profesionalismo de nuestros servicios.',
    'clients': 'Empresas líderes que han confiado en Corvus TID para sus soluciones de automatización y transformación digital.'
  };

  public logoSrc: string = 'assets/img/logo2-white.png';

  constructor(
    private menuConfigService: MenuConfigService,
    private router: Router
  ) { }

  ngAfterViewInit(): void {
    this.initHeroCanvas();
  }

  ngOnInit(): void {
    this.loadSections();

    // Escuchar cambios en la configuración de menús
    this.menuConfigService.onConfigChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadSections();
      });
  }

  ngOnDestroy(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    window.removeEventListener('resize', this.onHeroResize);

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga las secciones desde la configuración de menús
   */
  private loadSections(): void {
    const enabledMenus = this.menuConfigService.getPrimaryMenus();

    // Filtrar menús habilitados, excluyendo 'home' y 'contact'
    // 'contact' se excluye porque generalmente se pone como CTA al final
    this.sections = enabledMenus
      .filter(menu => menu.id !== 'home' && menu.id !== 'contact')
      .map(menu => ({
        title: menu.label,
        description: this.descriptionMapping[menu.id] || menu.description || `Conoce más sobre ${menu.label}`,
        route: menu.route,
        icon: this.iconMapping[menu.id] || 'iconsminds-shop-4'
      }));
  }

  navigateTo(route: string): void {
    this.router.navigateByUrl(route)
/*     window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; */
  }



  private initHeroCanvas(): void {
    const canvas = this.heroCanvas?.nativeElement;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    this.heroCtx = ctx;
    this.resizeHeroCanvas();
    this.createHeroDots();
    this.animateHeroDots();
    window.addEventListener('resize', this.onHeroResize);
  }

  private onHeroResize = (): void => {
    this.resizeHeroCanvas();
    this.createHeroDots();
  };

  private resizeHeroCanvas(): void {
    const canvas = this.heroCanvas?.nativeElement;
    if (!canvas) {
      return;
    }

    const heroContainer = canvas.parentElement;
    if (!heroContainer) {
      return;
    }

    const ratio = window.devicePixelRatio || 1;
    const width = heroContainer.clientWidth;
    const height = heroContainer.clientHeight;

    canvas.width = Math.max(1, Math.floor(width * ratio));
    canvas.height = Math.max(1, Math.floor(height * ratio));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    if (this.heroCtx) {
      this.heroCtx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }
  }

  private createHeroDots(): void {
    const canvas = this.heroCanvas?.nativeElement;
    if (!canvas) {
      return;
    }

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    this.heroDots = Array.from({ length: this.heroDotsCount }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      size: Math.random() * 1.8 + 0.6,
      alpha: Math.random() * 0.65 + 0.2,
    }));
  }

  private animateHeroDots(): void {
    const canvas = this.heroCanvas?.nativeElement;
    const ctx = this.heroCtx;

    if (!canvas || !ctx) {
      return;
    }

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    ctx.clearRect(0, 0, width, height);

    for (const dot of this.heroDots) {
      dot.x += dot.vx;
      dot.y += dot.vy;

      if (dot.x < 0 || dot.x > width) {
        dot.vx *= -1;
      }

      if (dot.y < 0 || dot.y > height) {
        dot.vy *= -1;
      }

      dot.x = Math.max(0, Math.min(width, dot.x));
      dot.y = Math.max(0, Math.min(height, dot.y));

      ctx.beginPath();
      ctx.arc(dot.x, dot.y, dot.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${dot.alpha})`;
      ctx.fill();
    }

    this.animationFrameId = requestAnimationFrame(() => this.animateHeroDots());
  }
}

